import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { siteInventoryService, beverageProductsService, measureDefinitionsService } from '../services';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '@/components/ui/collapsible';
import { useToast } from '@/hooks/use-toast';
import {
  Plus, Search, Loader2, Trash2, Package, ArrowUp, ArrowDown, ArrowUpDown,
  X, Pencil, Check, ChevronRight, Calendar, DollarSign, History, Copy,
} from 'lucide-react';
import { getApiError } from '@/lib/utils';
import CopyInventoryDialog from './CopyInventoryDialog';
import { measureTypeOf } from './shared/measureType';

const CATEGORIES = [
  { value: 'beer', label: 'Beer' },
  { value: 'wine', label: 'Wine' },
  { value: 'spirits', label: 'Spirits' },
  { value: 'soft_drinks', label: 'Soft Drinks' },
  { value: 'cider', label: 'Cider' },
  { value: 'mixers', label: 'Mixers' },
  { value: 'other', label: 'Other' },
];

function getProductDefaults(p) {
  const defUnit = p.units?.find(u => u.is_default_count_unit) || p.units?.[0];
  const defYield = p.yields?.find(y => y.is_default) || p.yields?.[0];
  return {
    unitType: defUnit?.unit_type || '—',
    size: defUnit?.conversion_to_base != null ? defUnit.conversion_to_base : '—',
    baseUnit: defUnit?.base_unit || '—',
    yieldVal: defYield?.nominal_servings_per_unit ?? defYield?.servings_per_unit ?? '—',
  };
}

// ── Price History Row (individual price line with edit/delete) ──
function PriceRow({ price, siteId, onUpdate, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    selling_price_incl_vat: price.selling_price_incl_vat,
    effective_from: price.effective_from,
  });
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const handleSave = async () => {
    if (!form.selling_price_incl_vat || isNaN(Number(form.selling_price_incl_vat))) {
      toast({ title: 'Invalid price', variant: 'destructive' });
      return;
    }
    if (!form.effective_from) {
      toast({ title: 'Date required', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const updated = await siteInventoryService.updateProductPrice(siteId, price.id, {
        selling_price_incl_vat: Number(form.selling_price_incl_vat),
        effective_from: form.effective_from,
      });
      onUpdate(updated);
      setEditing(false);
      toast({ title: 'Price updated' });
    } catch (err) {
      toast({ title: 'Update failed', description: getApiError(err, 'Error'), variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await siteInventoryService.deleteProductPrice(siteId, price.id);
      onDelete(price.id);
      toast({ title: 'Price deleted' });
    } catch (err) {
      toast({ title: 'Delete failed', description: getApiError(err, 'Error'), variant: 'destructive' });
    }
  };

  if (editing) {
    return (
      <div className="flex items-center gap-2 py-1" data-testid={`price-edit-row-${price.id}`}>
        <Input className="w-24 h-7 text-xs" type="date" value={form.effective_from}
          onChange={e => setForm(f => ({ ...f, effective_from: e.target.value }))} />
        <div className="flex items-center gap-0.5">
          <span className="text-xs text-muted-foreground">£</span>
          <Input className="w-20 h-7 text-xs text-right" type="number" step="0.01"
            value={form.selling_price_incl_vat}
            onChange={e => setForm(f => ({ ...f, selling_price_incl_vat: e.target.value }))} />
        </div>
        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={handleSave} disabled={saving}>
          <Check className="h-3 w-3 text-green-600" />
        </Button>
        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setEditing(false)}>
          <X className="h-3 w-3" />
        </Button>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between py-1 group" data-testid={`price-row-${price.id}`}>
      <div className="flex items-center gap-3">
        <span className="text-xs text-muted-foreground tabular-nums w-24">{price.effective_from}</span>
        <span className="text-xs font-medium tabular-nums">£{Number(price.selling_price_incl_vat).toFixed(2)}</span>
        <span className="text-[10px] text-muted-foreground">incl. VAT</span>
      </div>
      <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setEditing(true)}
          data-testid={`price-edit-btn-${price.id}`}>
          <Pencil className="h-3 w-3 text-muted-foreground" />
        </Button>
        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={handleDelete}
          data-testid={`price-delete-btn-${price.id}`}>
          <Trash2 className="h-3 w-3 text-destructive" />
        </Button>
      </div>
    </div>
  );
}

// ── Inventory Product Row (with expandable price history) ──
function InventoryProductRow({
  item, siteId, measureOverrides, prices, masterMeasures, onMeasureSaved, onMeasureDeleted,
  onPriceCreated, onPriceUpdated, onPriceDeleted, onToggleActive, onRemove, onCaseOverrideSaved,
}) {
  const { toast } = useToast();
  const pid = item.beverage_product_id;
  const measure = measureOverrides[pid];
  const productPrices = (prices[pid] || []).sort((a, b) =>
    b.effective_from.localeCompare(a.effective_from)
  );
  const latestPrice = productPrices[0];

  // Measure editing state
  const [editingMeasure, setEditingMeasure] = useState(false);
  const [measureForm, setMeasureForm] = useState({ measure_ml: '', measure_label: '', nominal_servings_per_unit: '' });
  const [savingMeasure, setSavingMeasure] = useState(false);

  // Price history expansion
  const [pricesOpen, setPricesOpen] = useState(false);
  const [addingPrice, setAddingPrice] = useState(false);
  const [newPriceForm, setNewPriceForm] = useState({
    selling_price_incl_vat: '',
    effective_from: new Date().toISOString().split('T')[0],
  });
  const [savingPrice, setSavingPrice] = useState(false);

  // iter420 Phase 3 — Inline price editor. Click the price text to flip
  // it into an editable Input; Enter or blur saves to today's date
  // against the product's default sales unit (or the explicit per-site
  // measure override if one's been set). Removes the screen-hop to
  // Site Bar Prices for the common "just set me a price" case.
  const [editingPrice, setEditingPrice] = useState(false);
  const [inlinePrice, setInlinePrice] = useState('');
  const [savingInline, setSavingInline] = useState(false);

  // ── Case override state ────────────────────────────────────────
  const [caseOpen, setCaseOpen] = useState(false);
  const [caseForm, setCaseForm] = useState({
    site_purchase_case_count: item.site_purchase_case_count ?? '',
    site_purchase_case_ml:    item.site_purchase_case_ml    ?? '',
    reason:                   item.case_override_reason     ?? '',
  });
  const [savingCase, setSavingCase] = useState(false);

  const saveCase = async () => {
    setSavingCase(true);
    try {
      const payload = {
        site_purchase_case_count: caseForm.site_purchase_case_count !== '' ? Number(caseForm.site_purchase_case_count) : null,
        site_purchase_case_ml:    caseForm.site_purchase_case_ml    !== '' ? Number(caseForm.site_purchase_case_ml)    : null,
        reason:                   caseForm.reason || null,
      };
      const updated = await siteInventoryService.setCaseOverride(siteId, pid, payload);
      onCaseOverrideSaved(pid, updated);
      setCaseOpen(false);
      toast({ title: 'Case size saved for this site' });
    } catch (err) {
      toast({ title: 'Failed', description: getApiError(err, 'Error'), variant: 'destructive' });
    } finally { setSavingCase(false); }
  };

  const clearCase = async () => {
    setSavingCase(true);
    try {
      const updated = await siteInventoryService.setCaseOverride(siteId, pid, { clear: true });
      onCaseOverrideSaved(pid, updated);
      setCaseForm({ site_purchase_case_count: '', site_purchase_case_ml: '', reason: '' });
      setCaseOpen(false);
      toast({ title: 'Reset to default — site setting removed' });
    } catch (err) {
      toast({ title: 'Failed', variant: 'destructive' });
    } finally { setSavingCase(false); }
  };
  // iter420g — SINGLE source of truth for the site-effective sales unit.
  // Backend resolves: site override (sales_unit_id) → past stocktake
  // entry → master is_default_sales_unit → first is_sales_unit → count
  // unit. This SAME unit drives the Measure column AND the Selling Price
  // description, so the page no longer shows three different measures
  // (e.g. "keg" / "half-pint" / "pint") for one product.
  const effectiveSalesUnit = useMemo(() => {
    const esu = item.effective_sales_unit;
    if (!esu || !esu.id) {
      // Pre-iter420g fallback for items that arrived before the backend
      // could resolve a unit (e.g. legacy product with no master units).
      return (item.units || []).find(u => u.is_default_sales_unit)
        || (item.units || []).find(u => u.is_sales_unit)
        || (item.units || []).find(u => u.is_default_count_unit)
        || null;
    }
    const master = (item.units || []).find(u => u.id === esu.id);
    if (master) return master;
    return {
      id: esu.id,
      unit_type: esu.unit_type,
      conversion_to_base: esu.conversion_to_base,
      is_default_sales_unit: false,
    };
  }, [item.effective_sales_unit, item.units]);
  // Keep the legacy name so the rest of the component renders without a
  // mass rename — points at the same resolved unit.
  const defaultSalesUnit = effectiveSalesUnit;
  const startInlinePriceEdit = () => {
    setInlinePrice(latestPrice ? String(Number(latestPrice.selling_price_incl_vat)) : '');
    setEditingPrice(true);
  };
  const cancelInlinePrice = () => {
    setEditingPrice(false);
    setInlinePrice('');
  };
  const commitInlinePrice = async () => {
    const val = parseFloat(inlinePrice);
    if (!Number.isFinite(val) || val < 0) {
      cancelInlinePrice();
      return;
    }
    // No-op if unchanged
    if (latestPrice && Math.abs(val - Number(latestPrice.selling_price_incl_vat)) < 0.005) {
      cancelInlinePrice();
      return;
    }
    setSavingInline(true);
    try {
      const measureMl = measure?.measure_ml
        || (defaultSalesUnit ? parseFloat(defaultSalesUnit.conversion_to_base) : null)
        || null;
      const result = await siteInventoryService.createProductPrice(siteId, pid, {
        selling_price_incl_vat: val,
        effective_from: new Date().toISOString().split('T')[0],
        measure_ml: measureMl,
      });
      onPriceCreated(pid, result);
      toast({
        title: 'Price saved',
        description: defaultSalesUnit
          ? `£${val.toFixed(2)} per ${defaultSalesUnit.unit_type} — effective today`
          : `£${val.toFixed(2)} — effective today`,
      });
      setEditingPrice(false);
      setInlinePrice('');
    } catch (err) {
      toast({ title: 'Failed', description: getApiError(err, 'Error'), variant: 'destructive' });
    } finally {
      setSavingInline(false);
    }
  };

  // Build measure options from product master units + organisation master measures
  const productUnits = item.units || [];
  const measureOptions = useMemo(() => {
    const seen = new Set();
    const opts = [];
    // Add product master units
    for (const u of productUnits) {
      const key = u.unit_type?.toLowerCase();
      if (key && !seen.has(key)) {
        seen.add(key);
        opts.push({
          label: u.unit_type.charAt(0).toUpperCase() + u.unit_type.slice(1),
          value: key,
          ml: u.conversion_to_base,
          // 2026-11 fix — carry base_unit too so count-based options
          // (Packet/Case) don't get a misleading "(1ml)"/"(12ml)" suffix
          // in the picker below.
          baseUnit: u.base_unit,
          // iter420g — Carry the master unit id so the editor can save
          // a canonical `sales_unit_id` (single source of truth) rather
          // than the legacy ml/label pair alone.
          unitId: u.id,
          isSalesUnit: !!u.is_sales_unit,
          isDefaultSalesUnit: !!u.is_default_sales_unit,
        });
      }
    }
    // Add master measure definitions from the organisation — these are
    // free-pour VOLUME concepts (Shot 25ml, Double 50ml, Can 125ml...).
    // iter815 fix — previously offered unconditionally, so a mass product
    // (e.g. a 15g Portion) listed a page of "ml" options that could never
    // apply to it and, if picked, mislabeled the value with a "g" suffix
    // that didn't match the chosen ml figure. Skip them entirely once any
    // of the product's own units are mass-based.
    const productIsMass = productUnits.some(u => measureTypeOf(u.base_unit) === 'mass');
    if (!productIsMass) {
      for (const mm of (masterMeasures || [])) {
        const key = mm.name.toLowerCase();
        if (!seen.has(key)) {
          seen.add(key);
          opts.push({ label: mm.name, value: key, ml: mm.default_ml });
        }
      }
    }
    // iter420k — Sort options alphabetically (case-insensitive) so the
    // dropdown order is predictable across the stocktake module.
    opts.sort((a, b) => (a.label || '').localeCompare((b.label || ''), undefined, { sensitivity: 'base' }));
    return opts;
  }, [productUnits, masterMeasures]);

  // ── Measure handlers ──
  const startEditMeasure = () => {
    setMeasureForm({
      measure_ml: measure?.measure_ml ?? '',
      measure_label: measure?.measure_label ?? '',
      nominal_servings_per_unit: measure?.nominal_servings_per_unit ?? '',
      // iter420g — Pre-fill the picker with the currently resolved
      // sales unit so the dropdown shows what the page is rendering.
      sales_unit_id: measure?.sales_unit_id ?? effectiveSalesUnit?.id ?? '',
    });
    setEditingMeasure(true);
  };

  const handleMeasureTypeSelect = (val) => {
    if (val === '__custom__') {
      setMeasureForm(f => ({ ...f, measure_ml: '', measure_label: '', sales_unit_id: '' }));
      return;
    }
    const opt = measureOptions.find(o => o.value === val);
    setMeasureForm(f => ({
      ...f,
      measure_label: opt?.label || val,
      measure_ml: opt?.ml || '',
      // iter420g — Match the picked option back to a master unit row so
      // we can persist the canonical `sales_unit_id`. Falls back to ''
      // (treated as null) when no master unit matches (legacy custom).
      sales_unit_id: opt?.unitId ?? '',
    }));
  };

  const saveMeasure = async () => {
    const hasMeasure = measureForm.measure_ml && !isNaN(Number(measureForm.measure_ml)) && Number(measureForm.measure_ml) > 0;
    const hasYield = measureForm.nominal_servings_per_unit
      && !isNaN(Number(measureForm.nominal_servings_per_unit))
      && Number(measureForm.nominal_servings_per_unit) > 0;
    const hasSalesUnit = !!measureForm.sales_unit_id;
    if (!hasMeasure && !hasYield && !hasSalesUnit) {
      toast({
        title: 'Pick a Sales Unit or enter a yield',
        description: 'Choose a master Sales Unit from the dropdown, OR enter a custom ml/yield — at least one is required.',
        variant: 'destructive',
      });
      return;
    }
    setSavingMeasure(true);
    try {
      const result = await siteInventoryService.upsertProductMeasure(siteId, pid, {
        // iter420g — `sales_unit_id` is the new canonical override.
        // Backend auto-fills measure_ml/measure_label from the picked
        // unit row, so legacy consumers stay in sync.
        sales_unit_id: hasSalesUnit ? Number(measureForm.sales_unit_id) : null,
        measure_ml: hasMeasure ? Number(measureForm.measure_ml) : null,
        measure_label: measureForm.measure_label || null,
        nominal_servings_per_unit: hasYield ? Number(measureForm.nominal_servings_per_unit) : null,
      });
      onMeasureSaved(pid, result);
      setEditingMeasure(false);
      toast({ title: 'Sales Unit saved' });
    } catch (err) {
      toast({ title: 'Failed', description: getApiError(err, 'Error'), variant: 'destructive' });
    } finally { setSavingMeasure(false); }
  };

  const deleteMeasure = async () => {
    try {
      await siteInventoryService.deleteProductMeasure(siteId, pid);
      onMeasureDeleted(pid);
      setEditingMeasure(false);
      toast({ title: 'Measure reset to product default' });
    } catch (err) {
      toast({ title: 'Failed', variant: 'destructive' });
    }
  };

  // ── Price handlers ──
  const addPrice = async () => {
    if (!newPriceForm.selling_price_incl_vat || isNaN(Number(newPriceForm.selling_price_incl_vat))) {
      toast({ title: 'Enter a valid price', variant: 'destructive' });
      return;
    }
    if (!newPriceForm.effective_from) {
      toast({ title: 'Date required', variant: 'destructive' });
      return;
    }
    setSavingPrice(true);
    try {
      const measureMl = measure?.measure_ml || null;
      const result = await siteInventoryService.createProductPrice(siteId, pid, {
        selling_price_incl_vat: Number(newPriceForm.selling_price_incl_vat),
        effective_from: newPriceForm.effective_from,
        measure_ml: measureMl,
      });
      onPriceCreated(pid, result);
      setAddingPrice(false);
      setNewPriceForm({ selling_price_incl_vat: '', effective_from: new Date().toISOString().split('T')[0] });
      toast({ title: 'Price added', description: `Effective from ${result.effective_from}` });
    } catch (err) {
      toast({ title: 'Failed', description: getApiError(err, 'Error'), variant: 'destructive' });
    } finally { setSavingPrice(false); }
  };

  // Display measure text — shows ml AND/OR yield override.
  // iter420g — Measure display uses the SAME `effectiveSalesUnit` as
  // the Selling Price column so the page shows ONE consistent label
  // per product (no more "keg" + "half-pint" + "pint" mismatch). The
  // site-specific yield override still surfaces alongside as a separate
  // bullet — different concern, different column row.
  //
  // 2026-11 fix — `conversion_to_base` is reused as a plain COUNT (1
  // for Packet, 12 for a 12-pack Case) for the packaged-snack count
  // pattern, not a real ml volume. This was showing "Packet — 1ml" even
  // though Product Master stores the product as `each`. Suppress the
  // "— Xml" suffix when the unit's base_unit classifies as count.
  // 2026-11 fix — same suppression now also applies to mass (g/kg):
  // `conversion_to_base` for a Portion unit is grams, not millilitres.
  const salesUnitMeasureType = effectiveSalesUnit ? measureTypeOf(effectiveSalesUnit.base_unit) : null;
  const measureParts = [];
  if (effectiveSalesUnit) {
    const ml = effectiveSalesUnit.conversion_to_base;
    const suffix = salesUnitMeasureType === 'volume' ? 'ml' : (salesUnitMeasureType === 'mass' ? 'g' : null);
    measureParts.push(`${effectiveSalesUnit.unit_type}${ml && suffix ? ` — ${ml}${suffix}` : ''}`);
  }
  if (measure?.nominal_servings_per_unit) {
    measureParts.push(`Yield: ${measure.nominal_servings_per_unit}/unit`);
  }
  const measureDisplay = measureParts.length ? measureParts.join(' · ') : 'Not set';
  // iter420g — "Default" badge means the row is using a master/inferred
  // unit (no explicit site override yet). Once an admin picks a site
  // Sales Unit via the editor, source becomes "site_override".
  const isDefaultMeasure = !item.effective_sales_unit
    || item.effective_sales_unit.source !== 'site_override';

  return (
    <>
      <TableRow data-testid={`inv-row-${pid}`}>
        <TableCell className="font-medium text-sm">
          <div>{item.product_name || `Product #${pid}`}</div>
          {/* Case size trigger — visible only when the lg Case column is hidden */}
          <button
            type="button"
            onClick={() => setCaseOpen(s => !s)}
            className="lg:hidden mt-0.5 text-[10px] leading-tight text-amber-700 hover:underline"
            data-testid={`case-toggle-sm-${pid}`}
          >
            Case:{' '}
            {item.site_purchase_case_count != null
              ? `${item.site_purchase_case_count}×${item.site_purchase_case_ml ? `${item.site_purchase_case_ml}ml` : '?'} (override)`
              : item.master_case_size != null
                ? `${item.master_case_size} (master)`
                : '—'
            }
          </button>
        </TableCell>
        <TableCell className="hidden md:table-cell text-xs text-muted-foreground">{item.brand || '—'}</TableCell>
        <TableCell className="hidden md:table-cell">
          {item.category ? <Badge variant="outline" className="text-[10px] capitalize">{item.category.replace('_',' ')}</Badge> : '—'}
        </TableCell>

        {/* ── Measure Column ── */}
        <TableCell data-testid={`inv-measure-${pid}`}>
          {editingMeasure ? (
            <div className="space-y-1.5" data-testid={`measure-editor-${pid}`}>
              <Select onValueChange={handleMeasureTypeSelect}
                value={(
                  measureOptions.find(o => o.unitId && String(o.unitId) === String(measureForm.sales_unit_id))
                  || measureOptions.find(o => (o.label || '').toLowerCase() === (measureForm.measure_label || '').toLowerCase())
                )?.value || ''}>
                <SelectTrigger className="h-7 text-xs w-full">
                  <SelectValue placeholder="Select measure type..." />
                </SelectTrigger>
                <SelectContent>
                  {measureOptions.map(o => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}{o.ml && measureTypeOf(o.baseUnit) === 'volume' ? ` (${o.ml}ml)` : (o.ml && measureTypeOf(o.baseUnit) === 'mass' ? ` (${o.ml}g)` : '')}
                    </SelectItem>
                  ))}
                  <SelectItem value="__custom__">Custom...</SelectItem>
                </SelectContent>
              </Select>
              <div className="flex items-center gap-1">
                <Input className="w-20 h-7 text-xs text-right" type="number" step="0.01"
                  placeholder={measureTypeOf(effectiveSalesUnit?.base_unit) === 'mass' ? 'g' : 'ml'} value={measureForm.measure_ml}
                  onChange={e => setMeasureForm(f => ({ ...f, measure_ml: e.target.value }))}
                  data-testid={`measure-ml-input-${pid}`} />
                <span className="text-[10px] text-muted-foreground">{measureTypeOf(effectiveSalesUnit?.base_unit) === 'mass' ? 'g' : 'ml'}</span>
                <Input className="flex-1 h-7 text-xs" placeholder="Label"
                  value={measureForm.measure_label}
                  onChange={e => setMeasureForm(f => ({ ...f, measure_label: e.target.value }))} />
              </div>
              <div className="flex items-center gap-1">
                <Input className="w-20 h-7 text-xs text-right" type="number" step="0.0001" min="0"
                  placeholder="Yield" value={measureForm.nominal_servings_per_unit}
                  onChange={e => setMeasureForm(f => ({ ...f, nominal_servings_per_unit: e.target.value }))}
                  data-testid={`measure-yield-input-${pid}`} />
                <span className="text-[10px] text-muted-foreground">servings / stock unit</span>
              </div>
              <p className="text-[10px] text-muted-foreground leading-tight">
                Override the master yield for this site only — useful for diluted products
                like Postmix where in-house pour differs by venue.
              </p>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="sm" className="h-6 text-[10px]" onClick={saveMeasure} disabled={savingMeasure}>
                  <Check className="h-3 w-3 mr-1" /> Save
                </Button>
                <Button variant="ghost" size="sm" className="h-6 text-[10px]" onClick={() => setEditingMeasure(false)}>
                  Cancel
                </Button>
                {measure && (
                  <Button variant="ghost" size="sm" className="h-6 text-[10px] text-destructive" onClick={deleteMeasure}>
                    <Trash2 className="h-3 w-3 mr-1" /> Reset
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-1.5 cursor-pointer group/measure" onClick={startEditMeasure}
              data-testid={`measure-display-${pid}`}>
              <span className="text-xs tabular-nums">{measureDisplay}</span>
              {isDefaultMeasure && <Badge variant="secondary" className="text-[9px] px-1">Default</Badge>}
              <Pencil className="h-3 w-3 text-muted-foreground opacity-0 group-hover/measure:opacity-100 transition-opacity" />
            </div>
          )}
        </TableCell>

        {/* ── Selling Price Column (iter420 Phase 3 — inline edit) ── */}
        <TableCell data-testid={`inv-price-${pid}`}>
          <div className="flex items-center gap-1.5">
            <div className="flex-1">
              {editingPrice ? (
                <div className="flex items-center gap-1">
                  <span className="text-xs text-muted-foreground">£</span>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    autoFocus
                    value={inlinePrice}
                    onChange={(e) => setInlinePrice(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') commitInlinePrice();
                      if (e.key === 'Escape') cancelInlinePrice();
                    }}
                    onBlur={commitInlinePrice}
                    disabled={savingInline}
                    className="h-6 text-xs w-20 tabular-nums"
                    data-testid={`inv-price-input-${pid}`}
                  />
                  {defaultSalesUnit && (
                    <span className="text-[10px] text-muted-foreground">
                      /{defaultSalesUnit.unit_type}
                    </span>
                  )}
                </div>
              ) : latestPrice ? (
                <button
                  type="button"
                  onClick={startInlinePriceEdit}
                  className="group/price text-left w-full"
                  data-testid={`inv-price-display-${pid}`}
                  title="Click to edit (saves with today's date against the product's default sales unit)"
                >
                  <span className="text-xs font-medium tabular-nums">
                    £{Number(latestPrice.selling_price_incl_vat).toFixed(2)}
                    <Pencil className="inline-block h-2.5 w-2.5 ml-1 text-muted-foreground opacity-0 group-hover/price:opacity-100 transition-opacity" />
                    <span className="text-[10px] text-muted-foreground ml-1">
                      {defaultSalesUnit ? `/${defaultSalesUnit.unit_type}` : ''} from {latestPrice.effective_from}
                    </span>
                  </span>
                </button>
              ) : (
                <button
                  type="button"
                  onClick={startInlinePriceEdit}
                  className="text-xs text-rose-600 hover:underline"
                  data-testid={`inv-price-set-${pid}`}
                >
                  Set price…
                </button>
              )}
            </div>
            <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0"
              onClick={() => setPricesOpen(!pricesOpen)}
              data-testid={`price-expand-${pid}`}
              title="Show full price history / multiple measures">
              <ChevronRight className={`h-3.5 w-3.5 transition-transform duration-200 ${pricesOpen ? 'rotate-90' : ''}`} />
            </Button>
          </div>
        </TableCell>

        <TableCell className="hidden lg:table-cell" data-testid={`inv-case-${pid}`}>
          <button
            type="button"
            onClick={() => setCaseOpen(!caseOpen)}
            className="flex items-center gap-1 text-xs hover:text-amber-700 transition-colors"
            data-testid={`case-toggle-${pid}`}
          >
            {/* Display format: count × unit_ml = total_ml/case.
                site_purchase_case_ml stores PER-UNIT ml (not case total).
                If both values exist we compute and show the case total inline
                so users can immediately spot a mismatch with the source data. */}
            <span className={item.site_purchase_case_count != null ? 'font-medium text-amber-700' : 'text-muted-foreground'}>
              {item.site_purchase_case_count != null
                ? (() => {
                    const cnt = item.site_purchase_case_count;
                    const ml  = item.site_purchase_case_ml;
                    if (cnt && ml) {
                      const caseTotal = cnt * ml;
                      return `${cnt}×${ml}ml (${caseTotal}ml/case)`;
                    }
                    if (cnt) return `${cnt} units`;
                    return '—';
                  })()
                : item.master_case_size != null ? `${item.master_case_size} (master)` : '—'}
            </span>
            <Pencil className="h-2.5 w-2.5 opacity-50" />
          </button>
        </TableCell>

        <TableCell className="text-center">
          <Switch checked={item.active_for_counting} onCheckedChange={() => onToggleActive(item)}
            data-testid={`inv-active-${pid}`} />
        </TableCell>
        <TableCell>
          <Button variant="ghost" size="icon" className="h-7 w-7"
            onClick={() => onRemove(pid, item.product_name)}
            data-testid={`inv-remove-${pid}`}>
            <Trash2 className="h-3.5 w-3.5 text-destructive" />
          </Button>
        </TableCell>
      </TableRow>

      {/* ── Inline Case Size Editor ── */}
      {caseOpen && (() => {
        const hasOverride = item.site_purchase_case_count != null || item.site_purchase_case_ml != null;
        const effectiveCount = item.site_purchase_case_count ?? item.master_case_size;
        const effectiveMl    = item.site_purchase_case_ml;
        const caseTotal      = (effectiveCount && effectiveMl) ? effectiveCount * effectiveMl : null;
        const effectiveLabel = effectiveCount != null
          ? `${effectiveCount}${effectiveMl ? ` × ${effectiveMl}ml = ${caseTotal}ml/case` : ' units/case'}`
          : '—';
        const defaultLabel   = item.master_case_size != null ? `${item.master_case_size} bottles/case` : 'not set';
        return (
          <TableRow data-testid={`case-override-panel-${pid}`}>
            <TableCell colSpan={8} className="py-2 px-4 bg-amber-50/50 border-l-2 border-amber-400">
              <div className="text-xs space-y-1.5">

                {/* Status line */}
                <div className="flex items-center gap-3 text-[11px]">
                  <span className="font-medium text-amber-800">Case size at this site</span>
                  <span className="text-slate-400">·</span>
                  <span className="text-slate-500">Default: <span className="font-mono">{defaultLabel}</span></span>
                  {hasOverride && (
                    <>
                      <span className="text-slate-400">·</span>
                      <span className="text-emerald-700 font-medium">In effect: <span className="font-mono">{effectiveLabel}</span></span>
                    </>
                  )}
                </div>

                {/* Compact edit row — all fields on one line */}
                <div className="flex flex-wrap items-center gap-2">
                  <label className="text-slate-500 whitespace-nowrap">Units/case</label>
                  <Input
                    type="number" min="1" step="1" className="h-7 w-16 text-xs"
                    placeholder={item.master_case_size ?? '—'}
                    value={caseForm.site_purchase_case_count}
                    onChange={e => setCaseForm(f => ({ ...f, site_purchase_case_count: e.target.value }))}
                    data-testid={`case-count-input-${pid}`}
                  />
                  <span className="text-slate-400">×</span>
                  <label className="text-slate-500 whitespace-nowrap">Unit size (ml)</label>
                  <div className="flex items-center gap-0.5">
                    <Input
                      type="number" min="1" step="1" className="h-7 w-20 text-xs"
                      placeholder="ml per unit"
                      value={caseForm.site_purchase_case_ml}
                      onChange={e => setCaseForm(f => ({ ...f, site_purchase_case_ml: e.target.value }))}
                      data-testid={`case-ml-input-${pid}`}
                    />
                    <span className="text-slate-400 text-[11px] ml-0.5">ml</span>
                  </div>
                  {/* Computed case total preview */}
                  {caseForm.site_purchase_case_count && caseForm.site_purchase_case_ml && (
                    <span className="text-[11px] text-slate-500 font-mono whitespace-nowrap">
                      = {Number(caseForm.site_purchase_case_count) * Number(caseForm.site_purchase_case_ml)}ml/case
                    </span>
                  )}
                  <Input
                    className="h-7 text-xs w-40 min-w-0" placeholder="Note (optional)"
                    value={caseForm.reason}
                    onChange={e => setCaseForm(f => ({ ...f, reason: e.target.value }))}
                    data-testid={`case-reason-input-${pid}`}
                  />
                  <div className="flex gap-1 ml-auto">
                    <Button size="sm" className="h-7 text-xs px-3" onClick={saveCase} disabled={savingCase}
                      data-testid={`case-override-save-${pid}`}>
                      {savingCase ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Check className="h-3 w-3 mr-1" />Save</>}
                    </Button>
                    {hasOverride && (
                      <Button size="sm" variant="outline" className="h-7 text-xs px-3 text-slate-600" onClick={clearCase} disabled={savingCase}
                        data-testid={`case-override-clear-${pid}`}>
                        Reset to default
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" className="h-7 text-xs px-3" onClick={() => setCaseOpen(false)}>Cancel</Button>
                  </div>
                </div>

              </div>
            </TableCell>
          </TableRow>
        );
      })()}

      {/* ── Expanded Price History ── */}
      {pricesOpen && (
        <TableRow data-testid={`price-history-${pid}`}>
          <TableCell colSpan={7} className="py-2 px-4 bg-muted/20">
            <div className="space-y-1">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium flex items-center gap-1.5">
                  <History className="h-3.5 w-3.5 text-muted-foreground" />
                  Price History ({productPrices.length} record{productPrices.length !== 1 ? 's' : ''})
                </span>
                <Button variant="outline" size="sm" className="h-6 text-[10px]"
                  onClick={() => setAddingPrice(true)}
                  data-testid={`add-price-btn-${pid}`}>
                  <Plus className="h-3 w-3 mr-1" /> Add Price
                </Button>
              </div>

              {/* Add new price form */}
              {addingPrice && (
                <div className="flex items-center gap-2 py-1.5 px-2 bg-background rounded border"
                  data-testid={`new-price-form-${pid}`}>
                  <Input className="w-24 h-7 text-xs" type="date" value={newPriceForm.effective_from}
                    onChange={e => setNewPriceForm(f => ({ ...f, effective_from: e.target.value }))} />
                  <div className="flex items-center gap-0.5">
                    <span className="text-xs text-muted-foreground">£</span>
                    <Input className="w-20 h-7 text-xs text-right" type="number" step="0.01"
                      placeholder="0.00" value={newPriceForm.selling_price_incl_vat}
                      onChange={e => setNewPriceForm(f => ({ ...f, selling_price_incl_vat: e.target.value }))}
                      data-testid={`new-price-input-${pid}`} />
                  </div>
                  <span className="text-[10px] text-muted-foreground">incl. VAT</span>
                  <Button variant="outline" size="sm" className="h-6 text-[10px]" onClick={addPrice} disabled={savingPrice}>
                    <Check className="h-3 w-3 mr-1" /> Save
                  </Button>
                  <Button variant="ghost" size="sm" className="h-6 text-[10px]" onClick={() => setAddingPrice(false)}>
                    Cancel
                  </Button>
                </div>
              )}

              {/* Price rows */}
              {productPrices.length === 0 ? (
                <p className="text-xs text-muted-foreground py-2">No prices defined yet. Click "Add Price" to set one.</p>
              ) : (
                <div className="divide-y">
                  {productPrices.map(p => (
                    <PriceRow key={p.id} price={p} siteId={siteId}
                      onUpdate={(updated) => onPriceUpdated(pid, updated)}
                      onDelete={(priceId) => onPriceDeleted(pid, priceId)} />
                  ))}
                </div>
              )}
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}


export default function SiteInventoryTab({ siteId, siteName }) {
  const { toast } = useToast();
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [masterProducts, setMasterProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [pickerCategory, setPickerCategory] = useState('');
  const [showPicker, setShowPicker] = useState(false);
  const [pickerLoading, setPickerLoading] = useState(false);
  // iter360f — Copy-to-other-sites dialog
  const [showCopyDialog, setShowCopyDialog] = useState(false);

  // Inventory table sorting
  const [invSort, setInvSort] = useState({ col: '', dir: 'asc' });
  const [invSearch, setInvSearch] = useState('');

  // Master measure definitions (loaded from backend)
  const [masterMeasures, setMasterMeasures] = useState([]);
  const [measureOverrides, setMeasureOverrides] = useState({});
  const [pricesByProduct, setPricesByProduct] = useState({});  // { productId: [price, ...] }

  const loadInventory = useCallback(async () => {
    if (!siteId) return;
    setLoading(true);
    try {
      const data = await siteInventoryService.listInventory(siteId);
      setInventory(data || []);
      // Load site-level measures and prices
      const [measures, prices, masterMeasureData] = await Promise.all([
        siteInventoryService.getProductMeasures(siteId).catch(() => []),
        siteInventoryService.getProductPrices(siteId).catch(() => []),
        measureDefinitionsService.list().catch(() => []),
      ]);
      setMasterMeasures(
        // iter420k — Pre-sort so the master measure portion of
        // measureOptions is alphabetical (final sort happens inside
        // measureOptions which also includes product-master units).
        (masterMeasureData || []).slice().sort((a, b) =>
          (a.name || '').localeCompare((b.name || ''), undefined, { sensitivity: 'base' })
        )
      );
      const measureMap = {};
      for (const m of measures) {
        measureMap[m.beverage_product_id] = m;
      }
      setMeasureOverrides(measureMap);
      // Group prices by product
      const pMap = {};
      for (const p of prices) {
        if (!pMap[p.beverage_product_id]) pMap[p.beverage_product_id] = [];
        pMap[p.beverage_product_id].push(p);
      }
      setPricesByProduct(pMap);
    } catch (err) {
      console.error('Failed to load inventory:', err);
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  useEffect(() => { loadInventory(); }, [loadInventory]);

  const openPicker = async () => {
    setShowPicker(true);
    setPickerLoading(true);
    try {
      const data = await beverageProductsService.listAll({ status: 'active' });
      setMasterProducts(Array.isArray(data) ? data : (data.items || []));
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to load products', variant: 'destructive' });
    } finally {
      setPickerLoading(false);
    }
  };

  const addProduct = async (product) => {
    if (inventory.some(i => i.beverage_product_id === product.id)) {
      toast({ title: 'Already Added', description: `${product.product_name} is already in this site's inventory`, variant: 'destructive' });
      return;
    }
    try {
      const updated = await siteInventoryService.addInventoryItem(siteId, { beverage_product_id: product.id });
      setInventory(updated);
      toast({ title: 'Added', description: `${product.product_name} added to inventory` });
    } catch (err) {
      toast({ title: 'Error', description: getApiError(err, 'Failed to add'), variant: 'destructive' });
    }
  };

  const removeProduct = async (productId, name) => {
    try {
      await siteInventoryService.removeInventoryItem(siteId, productId);
      setInventory(prev => prev.filter(i => i.beverage_product_id !== productId));
      toast({ title: 'Removed', description: `${name} removed from inventory` });
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to remove', variant: 'destructive' });
    }
  };

  const toggleActive = async (item) => {
    const updated = inventory.map(i =>
      i.id === item.id ? { ...i, active_for_counting: !i.active_for_counting } : i
    );
    setInventory(updated);
    try {
      await siteInventoryService.bulkSaveInventory(siteId, updated.map((it, idx) => ({
        beverage_product_id: it.beverage_product_id,
        active_for_counting: it.active_for_counting,
        default_location_id: it.default_location_id,
        display_sequence: idx,
      })));
    } catch (err) {
      loadInventory();
    }
  };

  // ── Callbacks for child rows ──
  const handleMeasureSaved = (pid, result) => {
    setMeasureOverrides(prev => ({ ...prev, [pid]: result }));
  };
  const handleMeasureDeleted = (pid) => {
    setMeasureOverrides(prev => {
      const next = { ...prev };
      delete next[pid];
      return next;
    });
  };
  const handlePriceCreated = (pid, result) => {
    setPricesByProduct(prev => ({
      ...prev,
      [pid]: [...(prev[pid] || []), result],
    }));
  };
  const handlePriceUpdated = (pid, updated) => {
    setPricesByProduct(prev => ({
      ...prev,
      [pid]: (prev[pid] || []).map(p => p.id === updated.id ? { ...p, ...updated } : p),
    }));
  };
  const handlePriceDeleted = (pid, priceId) => {
    setPricesByProduct(prev => ({
      ...prev,
      [pid]: (prev[pid] || []).filter(p => p.id !== priceId),
    }));
  };

  const handleCaseOverrideSaved = (pid, updatedItem) => {
    setInventory(prev => prev.map(i =>
      i.beverage_product_id === pid
        ? { ...i,
            site_purchase_case_count:  updatedItem.site_purchase_case_count,
            site_purchase_case_ml:     updatedItem.site_purchase_case_ml,
            case_override_reason:      updatedItem.case_override_reason,
            case_override_at:          updatedItem.case_override_at,
            effective_case_count:      updatedItem.effective_case_count,
            effective_case_ml:         updatedItem.effective_case_ml,
          }
        : i
    ));
  };

  // ── Picker filtering ──
  const inventoryProductIds = new Set(inventory.map(i => i.beverage_product_id));
  const filteredMaster = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return masterProducts.filter(p => {
      if (inventoryProductIds.has(p.id)) return false;
      if (pickerCategory && p.category !== pickerCategory) return false;
      if (!term) return true;
      return (
        p.product_name?.toLowerCase().includes(term) ||
        p.product_code?.toLowerCase().includes(term) ||
        p.brand?.toLowerCase().includes(term) ||
        p.subcategory?.toLowerCase().includes(term)
      );
    });
  }, [masterProducts, inventoryProductIds, searchTerm, pickerCategory]);

  // ── Inventory table sorting & filtering ──
  const toggleInvSort = (col) => {
    setInvSort(prev =>
      prev.col === col
        ? { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { col, dir: 'asc' }
    );
  };

  const sortedInventory = useMemo(() => {
    let items = [...inventory];
    if (invSearch) {
      const term = invSearch.toLowerCase();
      items = items.filter(i =>
        i.product_name?.toLowerCase().includes(term) ||
        i.product_code?.toLowerCase().includes(term) ||
        i.brand?.toLowerCase().includes(term) ||
        i.subcategory?.toLowerCase().includes(term)
      );
    }
    if (invSort.col) {
      const dir = invSort.dir === 'asc' ? 1 : -1;
      items.sort((a, b) => {
        const va = (a[invSort.col] || '').toString().toLowerCase();
        const vb = (b[invSort.col] || '').toString().toLowerCase();
        return va < vb ? -dir : va > vb ? dir : 0;
      });
    }
    return items;
  }, [inventory, invSort, invSearch]);

  const InvSortHead = ({ col, children, className = '' }) => {
    const active = invSort.col === col;
    return (
      <TableHead
        className={`cursor-pointer select-none hover:bg-muted/60 transition-colors text-xs ${className}`}
        onClick={() => toggleInvSort(col)}
      >
        <span className="inline-flex items-center gap-1">
          {children}
          {active ? (
            invSort.dir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
          ) : (
            <ArrowUpDown className="h-3 w-3 opacity-30" />
          )}
        </span>
      </TableHead>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="inventory-loading">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        <span className="ml-2 text-sm text-muted-foreground">Loading inventory...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="site-inventory-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{inventory.length} product{inventory.length !== 1 ? 's' : ''} in inventory</p>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowCopyDialog(true)}
            disabled={inventory.length === 0}
            data-testid="copy-inventory-btn"
            title={inventory.length === 0 ? 'No products to copy' : 'Copy this site\'s product assignments to other sites or groups'}
          >
            <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy to...
          </Button>
          <Button size="sm" onClick={openPicker} data-testid="add-inventory-btn">
            <Plus className="h-3.5 w-3.5 mr-1.5" /> Add Products
          </Button>
        </div>
      </div>

      <CopyInventoryDialog
        open={showCopyDialog}
        onOpenChange={setShowCopyDialog}
        sourceSiteId={siteId}
        sourceSiteName={siteName}
        onSuccess={() => loadInventory()}
      />

      {/* ── Product Picker ── */}
      {showPicker && (
        <div className="border rounded-lg p-3 space-y-3 bg-muted/20" data-testid="product-picker">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input className="pl-8 h-8 text-sm" placeholder="Search name, code, brand, sub-category..."
                value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} data-testid="picker-search"
                autoComplete="off" autoCorrect="off" spellCheck={false} name="site-inventory-picker-search" />
            </div>
            <Select value={pickerCategory || 'all'} onValueChange={(v) => setPickerCategory(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[130px] h-8 text-xs" data-testid="picker-filter-cat">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setShowPicker(false)}>
              <X className="h-4 w-4" />
            </Button>
          </div>
          {pickerLoading ? (
            <div className="py-4 text-center"><Loader2 className="h-4 w-4 animate-spin mx-auto" /></div>
          ) : filteredMaster.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-3">
              {searchTerm || pickerCategory ? 'No matching products' : 'All products already added'}
            </p>
          ) : (
            <>
              {/* iter367 — Count strip moved OUTSIDE the scroll container.
                  Previously it was a sticky element competing with the
                  table header for top-0 inside the same scroll box; the
                  result was a frozen table header overlapping the count
                  and (in some browsers) blocking the scroll-wheel from
                  reaching rows past the first viewport. */}
              <p className="text-xs text-muted-foreground px-3 py-1 bg-slate-50 border rounded-t-md border-b-0" data-testid="picker-count-strip">
                {filteredMaster.length} product{filteredMaster.length !== 1 ? 's' : ''} available
              </p>
              <div className="max-h-[400px] overflow-y-auto border rounded-b-md" data-testid="picker-scroll-container">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/30 text-xs sticky top-0 z-10">
                    <TableHead className="text-xs">Product Name</TableHead>
                    <TableHead className="text-xs hidden sm:table-cell">Code</TableHead>
                    <TableHead className="text-xs hidden md:table-cell">Brand</TableHead>
                    <TableHead className="text-xs hidden md:table-cell">Category</TableHead>
                    <TableHead className="text-xs hidden lg:table-cell">Sub-Category</TableHead>
                    <TableHead className="text-xs hidden lg:table-cell">Type</TableHead>
                    <TableHead className="text-xs hidden lg:table-cell text-right">Size</TableHead>
                    <TableHead className="text-xs hidden lg:table-cell">Unit</TableHead>
                    <TableHead className="text-xs hidden xl:table-cell text-right">Yield</TableHead>
                    <TableHead className="w-[40px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredMaster.map(p => {
                    const d = getProductDefaults(p);
                    return (
                      <TableRow key={p.id} className="cursor-pointer hover:bg-muted/40"
                        data-testid={`picker-product-${p.id}`}>
                        <TableCell className="text-sm font-medium py-1.5">{p.product_name}</TableCell>
                        <TableCell className="text-xs text-muted-foreground py-1.5 hidden sm:table-cell">{p.product_code || '—'}</TableCell>
                        <TableCell className="text-xs text-muted-foreground py-1.5 hidden md:table-cell">{p.brand || '—'}</TableCell>
                        <TableCell className="py-1.5 hidden md:table-cell">
                          {p.category ? <Badge variant="outline" className="text-[10px] capitalize">{p.category.replace('_', ' ')}</Badge> : '—'}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground py-1.5 hidden lg:table-cell">{p.subcategory || '—'}</TableCell>
                        <TableCell className="text-xs text-muted-foreground py-1.5 hidden lg:table-cell capitalize">{d.unitType}</TableCell>
                        <TableCell className="text-xs text-muted-foreground py-1.5 hidden lg:table-cell text-right tabular-nums">{d.size}</TableCell>
                        <TableCell className="text-xs text-muted-foreground py-1.5 hidden lg:table-cell">{d.baseUnit}</TableCell>
                        <TableCell className="text-xs text-muted-foreground py-1.5 hidden xl:table-cell text-right tabular-nums">{d.yieldVal}</TableCell>
                        <TableCell className="py-1.5">
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => addProduct(p)}
                            data-testid={`picker-add-${p.id}`}>
                            <Plus className="h-3.5 w-3.5 text-primary" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Inventory Table ── */}
      {inventory.length === 0 ? (
        <div className="text-center py-10 text-muted-foreground" data-testid="inventory-empty">
          <Package className="h-8 w-8 mx-auto mb-2 opacity-40" />
          <p className="text-sm">No products assigned to this site yet</p>
          <p className="text-xs mt-1">Click "Add Products" to select from the master list</p>
        </div>
      ) : (
        <div className="space-y-2">
          {inventory.length > 5 && (
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input className="pl-8 h-8 text-sm" placeholder="Filter inventory..."
                value={invSearch} onChange={(e) => setInvSearch(e.target.value)} data-testid="inv-search"
                autoComplete="off" autoCorrect="off" spellCheck={false} name="site-inventory-filter-search" />
            </div>
          )}
          <div className="border rounded-md overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/30">
                  <InvSortHead col="product_name">Product Name</InvSortHead>
                  <InvSortHead col="brand" className="hidden md:table-cell">Brand</InvSortHead>
                  <InvSortHead col="category" className="hidden md:table-cell">Category</InvSortHead>
                  <TableHead className="text-xs">Selling Measure</TableHead>
                  <TableHead className="text-xs">Selling Price (incl. VAT)</TableHead>
                  <TableHead className="text-xs hidden lg:table-cell">Case</TableHead>
                  <TableHead className="text-center text-xs">Active</TableHead>
                  <TableHead className="w-[40px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedInventory.map((item, idx) => (
                  <InventoryProductRow
                    key={item.id || idx}
                    item={item}
                    siteId={siteId}
                    measureOverrides={measureOverrides}
                    prices={pricesByProduct}
                    masterMeasures={masterMeasures}
                    onMeasureSaved={handleMeasureSaved}
                    onMeasureDeleted={handleMeasureDeleted}
                    onPriceCreated={handlePriceCreated}
                    onPriceUpdated={handlePriceUpdated}
                    onPriceDeleted={handlePriceDeleted}
                    onToggleActive={toggleActive}
                    onRemove={removeProduct}
                    onCaseOverrideSaved={handleCaseOverrideSaved}
                  />
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
}
